import { Briefcase, Plus } from "lucide-react";
import type { FC } from "react";

import { Skeleton } from "@/components/ui/skeleton";
import { TableRow } from "@/components/ui/table";

import type { MatterDTO } from "../../../../api/types/matter";
import { VisaTag } from "../../../../components/common/VisaTag";
import {
  asVisaValue,
  getPetitionerName
} from "../../../../utils/matterDisplay";
import { caseTypeLabel } from "../../../customCaseTypes/customCaseTypes";
import { AssignedUsersAvatarStack } from "../../../foreignNationals/components/AssignedUsersAvatarStack";
import { CaseStatusPill } from "../../../matters/components/CaseStatusPill";
import {
  ClientTable,
  TABLE_TEXT,
  type ClientTableColumn
} from "../../components/table/ClientTable";
import { ClientTableRow } from "../../components/table/ClientTableRow";
import { ClientTableCell, MutedCell } from "../../components/table/cells";
import { formatMdY } from "../../lib/format";
import { matterWorkflowFlags } from "./casesModel";

const COLUMNS: ClientTableColumn[] = [
  { id: "visa", header: "Visa Type" },
  { id: "deadline", header: "Filing Deadline" },
  { id: "status", header: "Status" },
  { id: "petitioner", header: "Petitioner" },
  { id: "assignment", header: "Assignment" },
  { id: "created", header: "Date Created" }
];

const SkeletonRow: FC = () => (
  <TableRow className="hover:bg-transparent">
    <ClientTableCell>
      <Skeleton className="h-3 w-16" />
    </ClientTableCell>
    <ClientTableCell>
      <Skeleton className="h-3 w-24" />
    </ClientTableCell>
    <ClientTableCell>
      <Skeleton className="h-3 w-20" />
    </ClientTableCell>
    <ClientTableCell>
      <Skeleton className="h-3 w-32" />
    </ClientTableCell>
    <ClientTableCell className="gap-1">
      <Skeleton className="size-5 rounded-xs" />
      <Skeleton className="size-5 rounded-xs" />
    </ClientTableCell>
    <ClientTableCell>
      <Skeleton className="h-3 w-20" />
    </ClientTableCell>
  </TableRow>
);

type Props = {
  matters: MatterDTO[];
  isLoading: boolean;
  isFiltered: boolean;
  error?: string | null;
  onRetry?: () => void;
  onOpen: (matter: MatterDTO) => void;
  onNewCase: () => void;
};

export const CasesTable: FC<Props> = ({
  matters,
  isLoading,
  isFiltered,
  error,
  onRetry,
  onOpen,
  onNewCase
}) => (
  <ClientTable
    ariaLabel="Client Cases"
    columns={COLUMNS}
    isLoading={isLoading}
    isEmpty={matters.length === 0}
    error={error}
    onRetry={onRetry}
    renderSkeletonRow={(i) => <SkeletonRow key={i} />}
    empty={
      isFiltered
        ? {
            title: "No Matching Cases",
            icon: Briefcase,
            message: "Try a different status filter."
          }
        : {
            title: "No Cases Yet",
            icon: Briefcase,
            message:
              "Start this client's first case to begin preparing a petition.",
            cta: "New Case",
            ctaIcon: Plus,
            onCta: onNewCase
          }
    }
  >
    {matters.map((matter) => {
      const visa = (matter.case?.visa ?? "").toString().trim();
      const { isRfe, workflowType } = matterWorkflowFlags(matter);
      const petitioner = getPetitionerName(matter);
      const assigned = matter.assigned_to_user_ids ?? [];
      return (
        <ClientTableRow
          key={`${matter.case_id}-${matter.individual_id}`}
          onActivate={() => onOpen(matter)}
        >
          <ClientTableCell>
            {visa ? (
              <VisaTag
                visaType={asVisaValue(visa)}
                size="sm"
                isRfe={isRfe}
                workflowType={workflowType}
                label={caseTypeLabel(matter.case)}
              />
            ) : (
              <span className={TABLE_TEXT.muted}>—</span>
            )}
          </ClientTableCell>
          <MutedCell>{formatMdY(matter.case?.filing_deadline, "-")}</MutedCell>
          <ClientTableCell>
            <CaseStatusPill status={matter.case?.status ?? ""} />
          </ClientTableCell>
          <ClientTableCell className="max-w-60">
            <span
              className={`block truncate ${TABLE_TEXT.muted}`}
              title={petitioner === "-" ? undefined : petitioner}
            >
              {petitioner}
            </span>
          </ClientTableCell>
          <ClientTableCell>
            {assigned.length > 0 ? (
              <AssignedUsersAvatarStack userIds={assigned} />
            ) : (
              <span className={TABLE_TEXT.muted}>-</span>
            )}
          </ClientTableCell>
          <MutedCell>{formatMdY(matter.case?.created_at, "-")}</MutedCell>
        </ClientTableRow>
      );
    })}
  </ClientTable>
);
